import { AnimatePresence, motion } from 'motion/react'
import * as React from 'react'
import { useCallback, useEffect, useRef } from 'react'

import { cn } from '@/utils'

const MIN_WIDTH = 320
const MIN_HEIGHT = 300
const VIEWPORT_PADDING = 8

export interface PanelRect {
  x: number
  y: number
  w: number
  h: number
}

function clampRect(x: number, y: number, w: number, h: number): PanelRect {
  const vw = window.innerWidth
  const vh = window.innerHeight

  const clampedW = Math.min(Math.max(MIN_WIDTH, w), vw - VIEWPORT_PADDING * 2)
  const clampedH = Math.min(Math.max(MIN_HEIGHT, h), vh - VIEWPORT_PADDING * 2)
  const clampedX = Math.max(VIEWPORT_PADDING, x)
  const clampedY = Math.max(VIEWPORT_PADDING, y)

  return { x: clampedX, y: clampedY, w: clampedW, h: clampedH }
}

interface FloatingPanelProps {
  open: boolean
  rect: PanelRect
  onRectChange: (rect: PanelRect) => void
  children: React.ReactNode
  className?: string
}

const FloatingPanel = React.forwardRef<HTMLDivElement, FloatingPanelProps>(
  ({ open, rect, onRectChange, children, className }, ref) => {
    const panelRef = useRef<HTMLDivElement | null>(null)
    const dragging = useRef<{
      startX: number
      startY: number
      startRect: PanelRect
    } | null>(null)

    const rectRef = useRef(rect)
    useEffect(() => {
      rectRef.current = rect
    }, [rect])

    const applyStyle = (r: PanelRect) => {
      const el = panelRef.current
      if (!el) return
      el.style.left = `${r.x}px`
      el.style.top = `${r.y}px`
      el.style.width = `${r.w}px`
      el.style.height = `${r.h}px`
    }

    const onPointerDown = useCallback((e: React.PointerEvent) => {
      e.preventDefault()
      dragging.current = {
        startX: e.clientX,
        startY: e.clientY,
        startRect: { ...rectRef.current },
      }
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    }, [])

    const onPointerMove = useCallback((e: React.PointerEvent) => {
      if (!dragging.current) return
      const { startX, startY, startRect } = dragging.current
      const dx = e.clientX - startX
      const dy = e.clientY - startY

      const bottomRightX = startRect.x + startRect.w
      const bottomRightY = startRect.y + startRect.h

      const newW = startRect.w - dx
      const newH = startRect.h - dy
      const newX = bottomRightX - Math.max(MIN_WIDTH, newW)
      const newY = bottomRightY - Math.max(MIN_HEIGHT, newH)

      const clamped = clampRect(newX, newY, newW, newH)
      clamped.w = bottomRightX - clamped.x
      clamped.h = bottomRightY - clamped.y

      rectRef.current = clamped
      applyStyle(clamped)
    }, [])

    const onPointerUp = useCallback(() => {
      if (!dragging.current) return
      dragging.current = null
      onRectChange(rectRef.current)
    }, [onRectChange])

    return (
      <AnimatePresence>
        {open && (
          <motion.div
            ref={(node) => {
              panelRef.current = node
              if (typeof ref === 'function') ref(node)
              else if (ref) ref.current = node
            }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            style={{
              left: rect.x,
              top: rect.y,
              width: rect.w,
              height: rect.h,
            }}
            className={cn(
              'fixed z-50 flex flex-col overflow-hidden rounded-lg border bg-white shadow-2xl',
              className,
            )}
          >
            <div
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              className="absolute left-0 top-0 z-10 flex h-5 w-5 cursor-nw-resize items-start justify-start p-1 group"
            >
              <svg
                width="8"
                height="8"
                viewBox="0 0 8 8"
                className="text-muted-foreground/40 group-hover:text-muted-foreground transition-colors"
              >
                <title>Drag to resize</title>
                <circle cx="1.5" cy="1.5" r="1" fill="currentColor" />
                <circle cx="5" cy="1.5" r="1" fill="currentColor" />
                <circle cx="1.5" cy="5" r="1" fill="currentColor" />
              </svg>
            </div>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    )
  },
)
FloatingPanel.displayName = 'FloatingPanel'

export { FloatingPanel }
